<template>
  <div v-if="sub.isActiveUntilCancelled && !sub.isEvergreenMonthly">
    {{ $t("SubscriptionInformationComponent.notApplicable") }}
  </div>
  <div v-if="isNotActiveYet(sub)">{{ t("SubscriptionInformationComponent.notActivatedYet") }}</div>
  <div v-else-if="isProcessingRenewal(sub)">{{ t("SubscriptionInformationComponent.processingRenewal") }}</div>
  <div v-else-if="isWebrootSubscriptionPurged || isAmazonPostExpiredWindow">
    {{ t("SubscriptionInformationComponent.expired") }}
  </div>
  <div v-else-if="isSafeSubscriptionClosed(sub)">{{ t("SubscriptionInformationComponent.closed") }}</div>
  <div
    v-else-if="isInSafeRenewalWindow || isOutsideSafeRenewalWindow || isBestBuyChurn || hasOutstandingInvoices"
    class="danger-text"
  >
    {{ t("SubscriptionInformationComponent.expired") }}
  </div>
  <ul v-else>
    <li>{{ nextPaymentDate() }}</li>
    <li v-if="!hideNextPaymentDateText">{{ t(nextPaymentDateText) }}</li>
  </ul>
</template>

<script lang="ts">
import { defineComponent, PropType, computed } from "vue";
import { logEvent } from "@/common/logger";
import { t } from "@/i18n";
import { ISubscription } from "@/common/api/unifiedPortal/interfaces";
import { ExpirationState, KeyCodeSeller } from "@/globalEnums";
import { isSafeSubscriptionClosed, isNotActiveYet, isProcessingRenewal } from "@/common/subscriptionHelper";
import {
  SUBSCRIPTION_LAST_AMAZON_EXPIRED_DAY,
  SUBSCRIPTION_MIN_EXPIRED_DAY,
  SUBSCRIPTION_MIN_GRACE_PERIOD_DAY,
} from "@/define";
import { formatDate } from "@/common/dateFormat";

const componentName = "NextPaymentColumnComponent";

export default defineComponent({
  name: componentName,

  props: {
    sub: {
      type: Object as PropType<ISubscription>,
      required: true,
    },
  },

  setup(props) {
    logEvent("created", componentName);

    const isSafeTrialPurged = computed(() => {
      return props.sub.isTrial && props.sub.computer?.dataHasBeenPurged;
    });

    const isInSafeRenewalWindow = computed(() => {
      return props.sub.expiration === ExpirationState.Expired;
    });

    const isOutsideSafeRenewalWindow = computed(() => {
      return props.sub.expiration === ExpirationState.FinalWarning;
    });

    const isBestBuyChurn = computed(() => {
      return props.sub.keyCodeSellerType === KeyCodeSeller.BestBuy && props.sub.expiration === ExpirationState.Purged;
    });

    const isAmazonPostExpiredWindow = computed(() => {
      return (
        props.sub.keyCodeSellerType === KeyCodeSeller.Amazon &&
        props.sub.expirationDays <= SUBSCRIPTION_LAST_AMAZON_EXPIRED_DAY
      );
    });

    const isWebrootSubscriptionPurged = computed(() => {
      return props.sub.keyCodeSellerType === KeyCodeSeller.Webroot && props.sub.expiration === ExpirationState.Purged;
    });

    function nextPaymentDate() {
      let nextPaymentDate = "";

      if (props.sub?.nextPaymentDate) {
        const date = new Date(props.sub.nextPaymentDate);
        nextPaymentDate = formatDate(date);
      }

      return nextPaymentDate;
    }

    const hideNextPaymentDateText = computed(() => {
      return props.sub.resellerId && props.sub.resellerId > 0 && !props.sub.isPartnerReferral;
    });

    const nextPaymentDateText = computed(() => {
      if (props.sub.isTrial) {
        return "SubscriptionInformationComponent.trial";
      }

      if (
        props.sub.isAutoRenewEnabled ||
        (props.sub.isEvergreenMonthly && !props.sub.isCancelledEvergreenSubscription)
      ) {
        return "SubscriptionInformationComponent.automatic";
      }

      return "SubscriptionInformationComponent.manual";
    });

    const hasOutstandingInvoices = computed(() => {
      return (
        props.sub.hasOutstandingInvoices &&
        props.sub.outstandingInvoice &&
        props.sub.outstandingInvoice.outstandingInvoiceDays <= SUBSCRIPTION_MIN_EXPIRED_DAY &&
        props.sub.outstandingInvoice.outstandingInvoiceDays >= SUBSCRIPTION_MIN_GRACE_PERIOD_DAY
      );
    });

    //Expose to template
    return {
      t,
      hasOutstandingInvoices,
      hideNextPaymentDateText,
      nextPaymentDate,
      nextPaymentDateText,
      isAmazonPostExpiredWindow,
      isWebrootSubscriptionPurged,
      isBestBuyChurn,
      isOutsideSafeRenewalWindow,
      isInSafeRenewalWindow,
      isSafeTrialPurged,
      isSafeSubscriptionClosed,
      isNotActiveYet,
      isProcessingRenewal,
    };
  },
});
</script>

<style scoped lang="css">
@import "@/styles/variables.css";

.danger-text {
  color: var(--danger);
}
</style>
